import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';

/**
 * Bloom, on a budget.
 *
 * The arena is graded dark and desaturated and every spell is additive, which is
 * precisely the setup bloom exists for: without it a Fire Ball is a bright shape,
 * and with it the thing burns. It is the largest visible return left for the
 * work, which is why it is here rather than something cleverer.
 *
 * It is also the first thing that would drop a weak machine off sixty frames a
 * second, so it is built to be cheap and to be turned off. The pass runs at half
 * resolution — bloom is a blur, and blurring a smaller buffer looks near enough
 * identical while costing a quarter as many pixels — and `setEnabled(false)`
 * falls back to rendering the scene straight to the canvas with no composer in
 * the path at all.
 *
 * Not selective. Selective bloom means a second render of the scene with
 * everything unlit blacked out, which is a whole extra pass over the geometry
 * for a game whose bright things are already almost all spells and torches.
 */

export interface BloomSettings {
  enabled: boolean;
  /** How much of the blurred buffer is added back. */
  strength: number;
  /** How far the glow spreads. */
  radius: number;
  /** Brightness a pixel needs before it blooms at all. */
  threshold: number;
  /** Fraction of the canvas the blur runs at. */
  scale: number;
}

export const DEFAULT_BLOOM: BloomSettings = {
  enabled: true,
  // Restrained on purpose. Spells are already additive and already the only
  // saturated things on screen; heavy bloom would wash out the silhouettes that
  // the whole visual grammar depends on being readable.
  strength: 0.55,
  radius: 0.4,
  // Above the arena's own materials, so stone does not glow — only fire, spells,
  // torches, shrines and the wand tip clear it.
  threshold: 0.85,
  scale: 0.5,
};

export class Postprocess {
  private composer: EffectComposer | null = null;
  private bloom: UnrealBloomPass | null = null;
  private settings: BloomSettings;

  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene: THREE.Scene;
  private readonly camera: THREE.Camera;

  constructor(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.Camera,
    settings: Partial<BloomSettings> = {},
  ) {
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;
    this.settings = { ...DEFAULT_BLOOM, ...settings };
    if (this.settings.enabled) this.build();
  }

  private build(): void {
    const size = this.renderer.getSize(new THREE.Vector2());
    const { strength, radius, threshold, scale } = this.settings;

    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(this.scene, this.camera));

    this.bloom = new UnrealBloomPass(
      new THREE.Vector2(Math.max(1, size.x * scale), Math.max(1, size.y * scale)),
      strength, radius, threshold,
    );
    this.composer.addPass(this.bloom);
    this.composer.setSize(size.x, size.y);
  }

  private teardown(): void {
    this.composer?.dispose();
    this.composer = null;
    this.bloom = null;
  }

  get enabled(): boolean {
    return this.settings.enabled;
  }

  /** Turning it off removes the composer entirely rather than passing through it. */
  setEnabled(on: boolean): void {
    if (on === this.settings.enabled) return;
    this.settings.enabled = on;
    if (on) this.build();
    else this.teardown();
  }

  setStrength(strength: number): void {
    this.settings.strength = strength;
    if (this.bloom) this.bloom.strength = strength;
  }

  setThreshold(threshold: number): void {
    this.settings.threshold = threshold;
    if (this.bloom) this.bloom.threshold = threshold;
  }

  resize(width: number, height: number): void {
    if (!this.composer) return;
    this.composer.setSize(width, height);
    this.bloom?.resolution.set(
      Math.max(1, width * this.settings.scale),
      Math.max(1, height * this.settings.scale),
    );
  }

  /** Draws the world. The viewmodel is drawn separately, on top, unbloomed. */
  render(): void {
    if (this.composer) this.composer.render();
    else this.renderer.render(this.scene, this.camera);
  }
}
